export type DateRangePreset = 'today' | 'yesterday' | '7d' | '30d' | 'custom';

/** `filtered` removes devices classified as internal/test; `raw` keeps every device. */
export type DataScope = 'filtered' | 'raw';
/** Restricts GA4 metrics to guest or registered devices (last known account type). */
export type Segment = 'all' | 'guest' | 'registered';
export interface QueryFilters { scope: DataScope; segment: Segment }
export const DEFAULT_FILTERS: QueryFilters = { scope: 'filtered', segment: 'all' };

export interface DateRange {
  preset: DateRangePreset;
  start: string;
  end: string;
  timezone: string;
}

export type DataKind = 'actual' | 'estimated' | 'mock';
export type Availability = 'available' | 'not_collected' | 'not_applicable';

export interface Metric {
  value: number | null;
  availability: Availability;
  kind: DataKind;
  unit: 'count' | 'percent' | 'ratio' | 'milliseconds' | 'seconds' | 'currency';
  /** Why the value is unavailable, shown instead of a bare "No data". */
  reason?: string;
}

export interface TimePoint {
  date: string;
  value: number | null;
  /** True when the day is not finalized (GA4 intraday or a pending daily export). */
  provisional?: boolean;
}

export interface Series {
  id: string;
  label: string;
  points: TimePoint[];
}

export interface DataEnvelope<T> {
  data: T | null;
  meta: {
    range: DateRange;
    generatedAt: string;
    source: 'mock' | 'api';
    completeness: 'complete' | 'partial' | 'empty';
    sources?: Array<{
      id: 'ga4' | 'backend' | 'performance' | 'crashlytics' | 'sessions' | 'billing' | 'auth' | 'receipts';
      status: 'ready' | 'unavailable' | 'error';
      kind: 'actual' | 'estimated';
      earliestDate: string | null;
      latestDate: string | null;
      latestCompleteDate: string | null;
      reason?: string;
      provisionalDates?: string[];
    }>;
    /** Filters the data was computed with. */
    filters?: QueryFilters;
    /** Days in the range that are not finalized. Empty or absent means every day is final. */
    provisionalDates?: string[];
    /**
     * Why each provisional day is not final: `in_progress` (today's streaming table), `daily_export_pending` (the day has ended, its
     * daily table is not written yet), `daily_export_missing` (an older day whose daily table never arrived), `adjacent_table` (the
     * day is complete but its first hours sit in the previous UTC day's table, which is not final). `table` is that UTC day.
     */
    provisionalDays?: Array<{ date: string; reason: string; table: string }>;
  };
}

export interface OverviewMetrics {
  /** Present when the Overview shows the filtered numbers next to their raw counterparts. */
  raw?: { newUsers: Metric };
  kpis: {
    registeredUsers: Metric; verifiedUsers: Metric; newRegistrations: Metric; newUsers: Metric;
    dau: Metric; wau: Metric; mau: Metric; receiptsScanned: Metric;
    receiptUsers: Metric; receiptsPerReceiptUser: Metric; priceSearches: Metric;
    priceComparisonUsers: Metric; pantryUsers: Metric; budgetUsers: Metric;
    shoppingListUsers: Metric; crashFreeUsers: Metric;
    uninstalls?: Metric; crashFreeSessions?: Metric; activeDevices?: Metric;
  };
  activeUsers: Series;
  receiptUsage: Series;
  featureAdoption: Array<{ feature: string; users: number }>;
  costTrend: Series;
}

export interface FunnelStep {
  id: string;
  label: string;
  users: number;
  conversionFromPrevious: number | null;
  conversionFromStart: number;
  dropOffFromPrevious: number | null;
}

export interface ActivationMetrics {
  /**
   * The registration funnel only: sign-up started, completed, verified. It does not know about receipts or any other product
   * action; product activation is `installFunnel`, which covers guests as well.
   */
  steps: FunnelStep[];
  /** Kept optional for older clients. No longer produced. */
  secondaryActions?: Array<{ action: string; users: number }>;
  /** Install funnel: first_open to first screen to first meaningful action, for devices installed in the range. */
  installFunnel?: InstallFunnel;
  /** Per-journey funnels over devices active in the range. */
  journeys?: JourneyFunnel[];
  /** First date each event the funnels rely on appears in the export. A step cannot be judged before its event existed. */
  instrumentation?: Array<{ event: string; firstSeen: string | null; devices: number }>;
  registrationNotes?: string[];
}

export interface ActivationWindow { eligible: number; activated: number; rate: Metric }

export interface InstallFunnelSegment {
  segment: 'all' | 'guest' | 'registered' | 'unknown';
  steps: FunnelStep[];
  /** Share of installs that took a first meaningful action within 24 hours / 7 days, judged only on installs old enough to have had the chance. */
  within24h?: ActivationWindow;
  within7d?: ActivationWindow;
  /** Installs that came back on a later calendar day, judged only for installs whose next day is final. */
  returned?: ActivationWindow;
  coreActivation?: Array<{ id: string; label: string; devices: number; share: number }>;
}
export interface InstallFunnel {
  /** All devices, then each known account type. */
  segments: InstallFunnelSegment[];
  /** Median and 90th percentile, in seconds, among devices that reached the step. Null when no device did. */
  timeToFirstScreen: { p50: Metric; p90: Metric };
  timeToFirstAction: { p50: Metric; p90: Metric };
  /** Which action came first for the devices that took one. */
  firstActions: Array<{ action: string; devices: number; share: number }>;
  /** Core feature activation: devices that did the action at least once after install. */
  coreActivation: Array<{ id: string; label: string; devices: number; share: number }>;
  notes: string[];
}

export interface JourneyFunnel { id: string; name: string; steps: FunnelStep[] }

export interface RetentionCohort {
  cohort: string;
  size: number;
  /** Percentage retained per period label. Null means the period has not matured yet, never zero. */
  periods: Array<number | null>;
  /** Devices behind each percentage, for the tooltip. Null when immature. */
  retained?: Array<number | null>;
  /** Devices in the cohort that sent app_remove after install. */
  uninstalledDevices?: number;
  medianHoursToUninstall?: number | null;
  /** True when every period for this cohort is final (no provisional day involved). */
  mature?: boolean;
}

export interface RetentionMetrics {
  headline: { d1: Metric; d7: Metric; d30: Metric };
  cohorts: RetentionCohort[];
  periodLabels: string[];
  /** Devices counted in each headline: only cohorts whose period has matured. */
  headlineBase?: { d1: number; d7: number; d30: number };
  activity?: {
    activeDevices: Metric; newDevices: Metric; returningDevices: Metric; mau: Metric; stickiness: Metric;
    frequency: Array<{ bucket: string; devices: number }>;
    recency: Array<{ bucket: string; devices: number }>;
    inactiveTrend: Series;
    activeTrend: Series;
  };
  uninstalls?: { devices: Metric; installs: Metric; rate: Metric; note: string };
}

export interface FeatureRow {
  id: string; name: string; uniqueUsers: number; activeUserShare: number;
  eventCount: number; eventsPerUser: number; repeatUsers: number; repeatRate: number;
}
export interface FeatureUsageMetrics {
  features: FeatureRow[];
  trends: Series[];
  /** Active devices in the range, the denominator behind every adoption share. */
  activeDevices?: Metric;
  journeys?: JourneyFunnel[];
}

export interface BreakdownRow { key: string; label: string; newInstalls: number; activeDevices: number; uninstalls: number; share: number | null }

export interface AcquisitionMetrics {
  kpis: {
    newInstalls: Metric; activeDevices: Metric;
    /** Devices with an uninstall signal in the range, whenever they were installed. A different population from newInstalls. */
    uninstalls: Metric;
    /** Uninstall signals from devices that installed inside the range (the only ones that belong to these installs). */
    uninstallsFromNewInstalls: Metric;
    /** Installs in the range with no uninstall signal yet: installs minus uninstallsFromNewInstalls. */
    installsWithoutUninstall: Metric;
    /** Active devices that installed inside the range, and the rest (installed earlier or before the export began). */
    activeFromNewInstalls: Metric; activeFromEarlierInstalls: Metric;
    /** Unfiltered counterparts, shown so the effect of the internal-device filter is always visible. */
    rawNewInstalls: Metric; rawUninstalls: Metric; excludedInstalls: Metric;
  };
  installsTrend: Series;
  rawInstallsTrend: Series;
  activeTrend: Series;
  uninstallsTrend: Series;
  breakdowns: Record<string, BreakdownRow[]>;
  breakdownLabels: Record<string, string>;
  attribution: {
    /** What can honestly be said about where installs came from. */
    status: 'available' | 'partial' | 'unavailable';
    firstTouch: BreakdownRow[];
    notes: string[];
    googlePlayConsole: { connected: false; reason: string };
  };
  exclusions: {
    scope: DataScope;
    registry: 'applied' | 'not_configured' | 'unavailable';
    devices: number;
    totalDevices: number;
    rules: Array<{ id: string; label: string; devices: number }>;
  };
}

export interface ReceiptMetrics {
  processed: Metric; successful: Metric; failed: Metric; duplicates: Metric;
  successRate: Metric; uniqueUsers: Metric; receiptsPerUser: Metric;
  avgItemsPerReceipt: Metric; avgProcessingDuration: Metric; p50Latency: Metric;
  p95Latency: Metric; cacheHitRate: Metric; enrichmentCalls: Metric;
  receiptsTrend: Series; outcomeTrends: Series[]; latencyTrend: Series[];
  retailers: Array<{ retailer: string; receipts: number; share: number }>;
}

export interface PriceComparisonMetrics {
  totalSearches: Metric; uniqueUsers: Metric; searchesPerUser: Metric;
  successfulSearches: Metric; noResultSearches: Metric; failures: Metric;
  noResultRate: Metric; avgResultsReturned: Metric; avgRetailersPerResult: Metric;
  avgLatency: Metric; p50Latency: Metric; p95Latency: Metric;
  browseRequests: Metric; browsePagesLoaded: Metric; avgProductsPerBrowse: Metric;
  loadMoreUsage: Metric;
  searchesTrend: Series; outcomeTrends: Series[]; latencyTrend: Series[];
}

export interface CostMetrics {
  totalCost: Metric; averageCostPerDay: Metric; aiCost: Metric;
  costPerActiveUser: Metric; costPerReceipt: Metric; costPerPriceSearch: Metric;
  aiCallsPerReceipt: Metric;
  /** AI usage from the backend log, cost from Cloud Billing. Both are measured; neither is an estimate. */
  aiCalls?: Metric; aiCostPerCall?: Metric; aiTrend?: Series;
  aiByModel?: Array<{ sku: string; cost: number; usage: number | null; unit: string | null }>;
  aiSource?: string;
  dailyCost: Series;
  byService: Array<{ service: string; cost: number; kind: 'actual' | 'estimated' }>;
  currency: 'USD' | 'EUR';
}

export interface ReliabilityMetrics {
  crashFreeUsers: Metric; crashCount: Metric; nonFatalErrors: Metric;
  crashFreeSessions?: Metric; sessions?: Metric; crashedSessions?: Metric; crashedUsers?: Metric;
  startup?: { p50: Metric; p95: Metric; count: Metric; byVersion: Array<{ version: string; count: number; p50Ms: number | null; p95Ms: number | null }> };
  crashesByVersion?: Array<{ version: string; build: string; crashes: number; crashedSessions: number; sessions: number | null }>;
  topIssues?: Array<{ issueId: string; title: string; type: string; crashes: number; sessions: number }>;
  /** What each Firebase export currently provides, so a missing metric explains itself. */
  exports?: Array<{ id: 'crashlytics' | 'performance' | 'sessions'; status: 'ready' | 'unavailable' | 'error'; reason: string | null; earliestDate: string | null }>;
  receiptFailureRate: Metric; priceSearchFailureRate: Metric;
  receiptLatency: Metric; receiptP95Latency: Metric;
  priceSearchLatency: Metric; priceSearchP95Latency: Metric;
  errorsTrend: Series[]; latencyTrend: Series[];
  performanceTraces?: Array<{ trace: string; count: Metric; p50Latency: Metric; p95Latency: Metric }>;
}

/**
 * Every call takes the range, an optional abort signal and optional filters. Filters default to
 * `DEFAULT_FILTERS` (internal devices excluded, all account types).
 */
export interface AnalyticsDataSource {
  readonly kind: 'mock' | 'api';
  getOverview(range: DateRange, signal?: AbortSignal, filters?: QueryFilters): Promise<DataEnvelope<OverviewMetrics>>;
  getAcquisition(range: DateRange, signal?: AbortSignal, filters?: QueryFilters): Promise<DataEnvelope<AcquisitionMetrics>>;
  getActivationFunnel(range: DateRange, signal?: AbortSignal, filters?: QueryFilters): Promise<DataEnvelope<ActivationMetrics>>;
  getRetention(range: DateRange, signal?: AbortSignal, filters?: QueryFilters): Promise<DataEnvelope<RetentionMetrics>>;
  getFeatureUsage(range: DateRange, signal?: AbortSignal, filters?: QueryFilters): Promise<DataEnvelope<FeatureUsageMetrics>>;
  getReceiptMetrics(range: DateRange, signal?: AbortSignal, filters?: QueryFilters): Promise<DataEnvelope<ReceiptMetrics>>;
  getPriceComparisonMetrics(range: DateRange, signal?: AbortSignal, filters?: QueryFilters): Promise<DataEnvelope<PriceComparisonMetrics>>;
  getCostMetrics(range: DateRange, signal?: AbortSignal, filters?: QueryFilters): Promise<DataEnvelope<CostMetrics>>;
  getSystemHealth(range: DateRange, signal?: AbortSignal, filters?: QueryFilters): Promise<DataEnvelope<ReliabilityMetrics>>;
}
